/**
 * THE API CLIENT. Routes and shapes DERIVED FROM `infra/`, not invented.
 * =====================================================================
 *
 * Every route, status code and field name below was read out of
 * `infra/lib/config/routes.ts` (the authoritative route table — eight routes, no bulk endpoint and
 * *"there must not be one"*) and the handlers in `infra/src/handlers/`, on 2026-09-03. Where a shape
 * is uncertain, this file says so instead of guessing.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE CENTRAL DESIGN POINT: **"NOT BUILT YET" IS NOT A NETWORK FAILURE.**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * Several of these endpoints deliberately answer `503` with a NAMED BLOCKER —
 * `PRD-0.12-salesforce-identity`, `C-42-cloudfront-signing-key`, `C-12-technician-attribution`,
 * `configuration-incomplete` (`infra/src/handlers/runtime.ts`, `BLOCKERS`). The server-side comment
 * is explicit about why: *"a technician with no jobs listed cannot tell 'you have no work today'
 * from 'the integration is down'."*
 *
 * If the client folds those into "network error", the client re-creates on the phone exactly the
 * ambiguity the server went to trouble to remove. So {@link ApiOutcome} keeps them apart as separate
 * variants, and they are apart all the way to the screen:
 *
 *   · `not-built-yet`  — a 503 WITH a `blocker` in the body. The dependency is unfinished. Retrying
 *                        in ten seconds will not help. The UI names the blocker.
 *   · `server-error`   — a 5xx, INCLUDING a 503 with no `blocker` field (that shape is CloudFront or
 *                        API Gateway shedding load, not one of ours). Retry is reasonable.
 *   · `offline`        — `navigator.onLine` was false before we tried.
 *   · `network-error`  — `fetch` rejected. Could be DNS, TLS, a captive portal, a dropped
 *                        connection. We genuinely do not know, and the variant says so.
 *   · `rejected`       — a 4xx with our `code` (`infra/src/handlers/http.ts` `badRequest`). The
 *                        request is wrong and will stay wrong.
 *   · `auth-not-configured` — the token provider is the stub, so NOTHING WAS SENT. See `auth.ts`.
 *
 * ⚠️ **AN UNRESOLVED INCONSISTENCY IN `infra/`, AND I HAVE NOT RESOLVED IT.**
 * `infra/lib/stacks/edge-stack.ts` routes CloudFront `api/*` to the REST API origin, and
 * `RestApiOrigin` carries the stage as its origin path — so a request for `/api/submissions` arrives
 * at API Gateway as `/{stage}/api/submissions`. But the route table declares the resource path as
 * `submissions`, with no `api` prefix, and I could find no CloudFront Function or origin-path rewrite
 * in `infra/lib/`. Either the paths need a rewrite that is not written, or the route table needs the
 * prefix. `photos/*` has its own behaviour and matches the declared `photos/{photoId}` exactly, which
 * is consistent with the prefix being the odd one out.
 *
 * I did not change `infra/` (out of scope) and I have not tested against a deployed stage (deploying
 * is prohibited for this task), so {@link API_BASE_PATH} is CONFIGURABLE and defaults to the
 * documented `/api`. If the rewrite turns out to be missing, this is one env var, not a rewrite of
 * this file. Do not treat the default as verified.
 *
 * NO `Content-Type` GUESSING, NO CORS, NO CREDENTIALS. One CloudFront distribution fronts the PWA,
 * `/api/*` and `/photos/*`, so everything is same-origin by design and there is no preflight — the
 * server file notes that adding CORS headers *"would create the cross-origin surface the architecture
 * was chosen to avoid"*. `credentials: 'omit'` because authorization is a bearer token, never a
 * cookie; a cookie on this origin would be sent to the photo redirect too.
 */

import { tokenProvider } from './auth';

/** Overridable for a dev proxy or if the `api` prefix question above resolves the other way. */
export const API_BASE_PATH: string =
  (import.meta.env?.VITE_API_BASE_PATH as string | undefined) ?? '/api';

/**
 * The photo redirect is NOT under the API prefix: `infra/lib/stacks/edge-stack.ts` gives `photos/*`
 * its own CloudFront behaviour, because the URL Salesforce stores permanently is
 * `https://<app>/photos/{id}`.
 */
export const PHOTO_BASE_PATH: string =
  (import.meta.env?.VITE_PHOTO_BASE_PATH as string | undefined) ?? '/photos';

// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------

export type ApiOutcome<T> =
  | { readonly kind: 'ok'; readonly status: number; readonly data: T }
  /** 202 from `POST /submissions`. Kept distinct from 200 because it means "durably accepted, not written". */
  | { readonly kind: 'accepted'; readonly status: 202; readonly data: T }
  /** 4xx with our own `code`. Permanent: the request is malformed or refused by policy. */
  | {
      readonly kind: 'rejected';
      readonly status: number;
      readonly code: string;
      readonly requestId: string | null;
      readonly issues?: readonly unknown[];
    }
  /** 404 — "no such record" and "not yours" are the same answer by design (T-08). */
  | { readonly kind: 'not-found'; readonly status: 404; readonly requestId: string | null }
  /** 409 — idempotency-key conflict. Should be unreachable; loud if it happens. */
  | { readonly kind: 'conflict'; readonly status: 409; readonly code: string; readonly requestId: string | null }
  /** 401/403 from the authorizer. The session is not usable; not a retryable transport problem. */
  | { readonly kind: 'unauthenticated'; readonly status: number }
  /** 503 WITH a named blocker: an unfinished dependency. NOT a network failure. */
  | {
      readonly kind: 'not-built-yet';
      readonly status: 503;
      readonly blocker: string;
      readonly requestId: string | null;
      readonly retryAfterSeconds: number | null;
    }
  /** Any other 5xx, and a 503 with no blocker (CloudFront/API Gateway, not one of ours). */
  | { readonly kind: 'server-error'; readonly status: number; readonly requestId: string | null }
  /** We knew we were offline and did not send. */
  | { readonly kind: 'offline' }
  /** `fetch` rejected. Cause unknown, and the variant does not pretend otherwise. */
  | { readonly kind: 'network-error'; readonly detail: string }
  /** The token provider is the stub. **Nothing was sent.** */
  | { readonly kind: 'auth-not-configured' };

/** True when the outcome means "try this again later" rather than "this will never work". */
export function isRetryable(outcome: ApiOutcome<unknown>): boolean {
  switch (outcome.kind) {
    case 'server-error':
    case 'offline':
    case 'network-error':
    case 'not-built-yet':
      return true;
    case 'ok':
    case 'accepted':
    case 'rejected':
    case 'not-found':
    case 'conflict':
    case 'unauthenticated':
    case 'auth-not-configured':
      return false;
  }
}

// ---------------------------------------------------------------------------
// Response shapes — read off the handlers, field for field
// ---------------------------------------------------------------------------

/** `POST /submissions` → 202. `infra/src/handlers/submission-intake.ts`. */
export interface SubmissionAccepted {
  readonly submissionId: string;
  readonly status: string;
  readonly receivedAt: string;
  /** The CANONICAL form of the key we sent. May differ from what we sent; that is the contract. */
  readonly idempotencyKey: string;
  readonly queued: { readonly messageId: string };
}

/** One row of `GET /submissions`. */
export interface SubmissionListItem {
  readonly submissionId: string;
  readonly recordType: string;
  readonly kioskId: string | null;
  readonly status: string;
  readonly receivedAt: string;
  readonly claimedCapturedAt: string | null;
  readonly photoCount: number;
  readonly submissionSource: string | null;
}

export interface SubmissionListPage {
  readonly items: readonly SubmissionListItem[];
  readonly nextCursor: string | null;
}

/** `GET /submissions/{id}`. No photo keys and no URLs, by design — a count only. */
export interface SubmissionDetail extends SubmissionListItem {
  readonly externalSubmissionId: string | null;
}

/**
 * One row of `GET /interventions`. `recovery` is the pre-travel warning and is present on EVERY
 * row — `flagged: false` carries a reason, so a missing warning is explainable.
 */
export interface InterventionListItem {
  readonly interventionId: string;
  readonly name: string | null;
  readonly type: string | null;
  readonly subType: string | null;
  readonly vendorState: string | null;
  /** `false` on `Completed`: visible, read-only. Hiding it would look like the work vanished. */
  readonly actionable: boolean;
  readonly kioskId: string | null;
  readonly ticketId: string | null;
  readonly scheduledDate: string | null;
  readonly lockerClass: string | null;
  /**
   * `Supplier_WorkOrderId__c`. ⚠️ **0.18% filled** (44 of the 25,080 rows the work-list predicate
   * admits in Dev1, measured 2026-09-03). It is the right field for "the vendor's own reference"
   * and it is almost always blank, so the technician still types it.
   */
  readonly supplierWorkOrderId: string | null;
  /** Emitted only when > 0. */
  readonly rescheduleCount?: number;
  readonly recovery: RecoveryVerdict;

  /* ---- what a technician cannot type, so it must travel with the job ---- */

  /**
   * Host account name — *"the name of the location where you are installing the locker"*.
   * `Intervention__c.Account_Name__c`, **98.57%** of the visible queue and **97.0%** of
   * Survey-type rows.
   */
  readonly locationName: string | null;
  /**
   * `Intervention__c.Installation_Id__c`, **98.93%**. Carried and displayed; bound to no form
   * field — see `src/compose/intervention.ts`.
   */
  readonly installationId: string | null;
  /** `Case__r.CaseNumber`, **26.70%** (because `Case__c` itself is 26.70% filled). */
  readonly caseNumber: string | null;
  /**
   * `Provisioned_With_Installation__c`. ⚠️ `true` on **0 of 185,591** Dev1 rows, so in practice
   * the install form's first question can only ever be derived as `Installation` or
   * `Provisioning`, never `Installation & Provisioning`.
   */
  readonly provisionedWithInstallation: boolean | null;
}

export type RecoveryVerdict =
  | {
      readonly flagged: true;
      readonly code: 'kiosk-recovered-before-visit';
      readonly dispatchedFor: string;
      readonly kioskStatus: string;
      readonly kioskSubStatus: string | null;
      readonly advice: string;
    }
  | {
      readonly flagged: false;
      readonly reason:
        | 'sub-type-not-hard-offline'
        | 'sub-type-missing'
        | 'kiosk-not-in-service'
        | 'kiosk-status-missing';
    };

export interface InterventionListPage {
  readonly items: readonly InterventionListItem[];
  readonly nextCursor: string | null;
  readonly _meta: {
    /** Non-zero means the server's SOQL predicate and its own state classifier disagree. Show it. */
    readonly droppedByStateFilter: number;
    readonly recoveryFlaggedCount: number;
  };
}

/**
 * `GET /kiosks/{kioskId}` — the C-15 reduced payload (~20 keys out of a 355-field object).
 *
 * Typed loosely on purpose in the nested blocks: `reduceKioskPayload` omits any field that is empty,
 * so almost everything is optional, and `noUncheckedIndexedAccess` plus these `?`s force the UI to
 * handle absence. `hours.verified` is the literal `false` — Salesforce's `MON_`…`SUN_` fields are
 * timezone-less and an Italian record read 16:00–03:59, so the payload declares them unverified and
 * the UI must render "store hours (unverified)".
 */
export interface KioskPayload {
  readonly kioskId: string;
  readonly name?: string;
  readonly status?: { readonly state: string; readonly subState?: string };
  readonly hardware?: Readonly<Record<string, string | number | undefined>>;
  readonly location?: Readonly<Record<string, string | undefined>>;
  readonly access?: Readonly<Record<string, string | undefined>>;
  readonly hours?: {
    readonly verified: false;
    readonly note: string;
    readonly week: Readonly<Record<string, { open: string; close: string } | undefined>>;
  };
  readonly siteContact?: { readonly email?: string; readonly mobile?: string };
  readonly caseNumber?: string;
  readonly prefill?: { readonly installMethod?: string };
  readonly recovery?: RecoveryVerdict;
  readonly _meta: {
    readonly retrievedAt: string;
    readonly omittedEmptyFields: number;
    readonly payloadContract: 'reduced-c15';
  };
}

/** `POST /photos/mint`. The bucket and the object key are deliberately NOT returned (C-37). */
export interface PhotoUploadTarget {
  readonly photoId: string;
  readonly uploadUrl: string;
  readonly expiresAt: string;
  /** Signed INTO the URL: a PUT with different values is refused by S3. Send these verbatim. */
  readonly requiredHeaders: {
    readonly 'Content-Type': string;
    readonly 'Content-Length': string;
  };
}

// ---------------------------------------------------------------------------
// The transport
// ---------------------------------------------------------------------------

export interface RequestOptions {
  readonly method?: 'GET' | 'POST';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | undefined | null>>;
  readonly signal?: AbortSignal;
  /** Injected in tests. Defaults to the global. */
  readonly fetchImpl?: typeof fetch;
  /** Injected in tests. Defaults to `navigator.onLine`, treating "unknown" as online. */
  readonly onLine?: boolean;
}

interface ErrorBody {
  readonly error?: string;
  readonly code?: string;
  readonly blocker?: string;
  readonly requestId?: string;
  readonly issues?: readonly unknown[];
}

/**
 * The one place a request is made.
 *
 * The order of the checks is deliberate: **auth, then online, then send.** Checking online first
 * would report "offline" for an app that has no session, which sends a technician to look at their
 * signal bars over a problem that is ours.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiOutcome<T>> {
  const provider = tokenProvider();
  if (provider.kind === 'stub') {
    // Nothing is sent. See `auth.ts` — a stub must never produce something that looks like a
    // session, and an unauthenticated request would come back 401 and read like an expired one.
    return { kind: 'auth-not-configured' };
  }

  const token = await provider.getAccessToken();
  if (token === null) {
    return { kind: 'unauthenticated', status: 401 };
  }

  const online = options.onLine ?? navigatorOnLine();
  if (!online) {
    return { kind: 'offline' };
  }

  const doFetch = options.fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== 'function') {
    return { kind: 'network-error', detail: 'fetch unavailable in this runtime' };
  }

  const url = buildUrl(path, options.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await doFetch(url, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'omit',
      // `manual` so a 302 from the photo redirect is visible to us rather than silently followed
      // into the signed-URL origin, where an opaque failure would be uninterpretable.
      redirect: 'manual',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // Everything from DNS to a captive portal to a dropped TCP connection arrives here as one
    // opaque rejection. We do not know which, and we do not claim to.
    return { kind: 'network-error', detail: error instanceof Error ? error.message : String(error) };
  }

  return interpret<T>(response);
}

async function interpret<T>(response: Response): Promise<ApiOutcome<T>> {
  const status = response.status;

  if (status === 200) {
    return { kind: 'ok', status, data: (await readJson(response)) as T };
  }
  if (status === 202) {
    return { kind: 'accepted', status: 202, data: (await readJson(response)) as T };
  }

  const body = ((await readJson(response)) ?? {}) as ErrorBody;
  const requestId = typeof body.requestId === 'string' ? body.requestId : null;

  if (status === 401 || status === 403) {
    return { kind: 'unauthenticated', status };
  }
  if (status === 404) {
    return { kind: 'not-found', status: 404, requestId };
  }
  if (status === 409) {
    return { kind: 'conflict', status: 409, code: body.code ?? 'conflict', requestId };
  }
  if (status === 503) {
    // THE DISTINCTION THIS WHOLE FILE IS ORGANISED AROUND. `blocker` present → one of ours, and it
    // names an unfinished dependency. Absent → CloudFront or API Gateway shedding load, which is a
    // transport-shaped problem and must not be reported as "not built".
    if (typeof body.blocker === 'string' && body.blocker.length > 0) {
      const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
      return {
        kind: 'not-built-yet',
        status: 503,
        blocker: body.blocker,
        requestId,
        retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : null,
      };
    }
    return { kind: 'server-error', status, requestId };
  }
  if (status >= 500) {
    return { kind: 'server-error', status, requestId };
  }
  if (status >= 400) {
    return {
      kind: 'rejected',
      status,
      code: body.code ?? body.error ?? `http-${status}`,
      requestId,
      ...(body.issues !== undefined ? { issues: body.issues } : {}),
    };
  }

  // 1xx/2xx-other/3xx. A 302 reaches here (the photo redirect), and so would anything unexpected.
  // Reported as a server error rather than silently treated as success.
  return { kind: 'server-error', status, requestId };
}

async function readJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (text.trim().length === 0) {
      return null;
    }
    return JSON.parse(text) as unknown;
  } catch {
    // A non-JSON body from a proxy or an error page. The status is still meaningful, so the caller
    // gets its outcome with no body rather than an exception.
    return null;
  }
}

function navigatorOnLine(): boolean {
  const nav: Navigator | undefined = globalThis.navigator;
  // `undefined` (a non-browser runtime) is treated as ONLINE. Treating unknown as offline would
  // make the flusher refuse to run in any environment that lacks `navigator`.
  return nav === undefined || nav.onLine !== false;
}

function buildUrl(path: string, query?: Readonly<Record<string, string | number | undefined | null>>): string {
  const base = path.startsWith('/') ? path : `${API_BASE_PATH}/${path}`;
  if (query === undefined) {
    return base;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && String(value).length > 0) {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `${base}?${qs}` : base;
}

// ---------------------------------------------------------------------------
// The eight routes
// ---------------------------------------------------------------------------

/**
 * `GET /interventions` — the entry point of the app. PRD §4.3: the KID is not on the locker, so the
 * work order is the entry point, not the number.
 *
 * ⚠️ Expect `not-built-yet` with blocker `PRD-0.12-salesforce-identity` until the Salesforce read
 * identity exists. The route answers 503 rather than an empty list precisely so this is not mistaken
 * for "you have no work today".
 */
export function listInterventions(
  params: { limit?: number; cursor?: string | null } = {},
  options: RequestOptions = {},
): Promise<ApiOutcome<InterventionListPage>> {
  return apiRequest<InterventionListPage>('interventions', {
    ...options,
    query: { limit: params.limit, cursor: params.cursor },
  });
}

/**
 * `GET /kiosks/{kioskId}` — reduced payload. `kioskId` is `[0-9]{6,10}` — the same rule as the
 * server's `KIOSK_ID_PATTERN` in `infra/src/handlers/kiosk-read.ts`, which 400s anything else, and
 * this checks first so a typo in the manual-entry fallback is a local message rather than a round
 * trip. The rule comes from a live census of `Intervention__c.Kiosk_ID__c` (2026-09-04, 148,745
 * non-null rows): all values numeric, lengths 6–9, with `Asset.Kiosk_ID__c` declared `string(10)`
 * giving 10 as headroom.
 *
 * `interventionSubType` is a hint for the recovery check, not an authorization input.
 */
export function getKiosk(
  kioskId: string,
  params: { interventionSubType?: string | null } = {},
  options: RequestOptions = {},
): Promise<ApiOutcome<KioskPayload>> {
  if (!/^[0-9]{6,10}$/.test(kioskId)) {
    return Promise.resolve({
      kind: 'rejected',
      status: 400,
      code: 'malformed-kiosk-id',
      requestId: null,
    });
  }
  return apiRequest<KioskPayload>(`kiosks/${encodeURIComponent(kioskId)}`, {
    ...options,
    query: { interventionSubType: params.interventionSubType },
  });
}

export function listSubmissions(
  params: { limit?: number; cursor?: string | null } = {},
  options: RequestOptions = {},
): Promise<ApiOutcome<SubmissionListPage>> {
  return apiRequest<SubmissionListPage>('submissions', {
    ...options,
    query: { limit: params.limit, cursor: params.cursor },
  });
}

export function getSubmission(
  submissionId: string,
  options: RequestOptions = {},
): Promise<ApiOutcome<SubmissionDetail>> {
  return apiRequest<SubmissionDetail>(`submissions/${encodeURIComponent(submissionId)}`, options);
}

/**
 * The body of `POST /submissions`, as the intake handler reads it.
 *
 * WHAT IS ABSENT IS THE INTERESTING PART, and every absence is enforced server-side:
 *  · no technician name or email — identity comes from the verified token (C-12);
 *  · no supplier id in any form — the scope is minted from the authorizer's output (T-12);
 *  · no photo object key or prefix — server-generated (C-37);
 *  · no `receivedAt` — the server clock is the only clock. `capturedAt` is accepted and stored as a
 *    CLAIMED value (AC-3), never presented as fact;
 *  · `answers` must be a FLAT map of scalars. A nested object or array is the mass-assignment shape
 *    (T-11) and is refused with `answers-not-flat`.
 */
export interface SubmissionIntakeBody {
  readonly recordType: string;
  /** Generated once at enqueue by the outbox. Never mint one at send time. */
  readonly idempotencyKey: string;
  readonly kioskId?: string | null;
  readonly ticketId?: string | null;
  /** ISO-8601. A CLAIM about when the work happened, and stored as such. */
  readonly capturedAt?: string | null;
  readonly answers: Readonly<Record<string, string | number | boolean | null>>;
}

/**
 * `POST /submissions` → 202 `accepted`.
 *
 * ⚠️ Today this route answers **503 `C-12-technician-attribution`** on every call, because the
 * authorizer's context carries no verified `email` claim. Note what the handler does before it
 * refuses: **it has already persisted the submission row.** So a `not-built-yet` here does NOT mean
 * the work was lost, and a retry with the same idempotency key returns the same row rather than
 * creating a second one. That is why `sync.ts` keeps such an item queued instead of failing it.
 */
export function createSubmission(
  body: SubmissionIntakeBody,
  options: RequestOptions = {},
): Promise<ApiOutcome<SubmissionAccepted>> {
  return apiRequest<SubmissionAccepted>('submissions', { ...options, method: 'POST', body });
}

/** `POST /photos/mint` — a presigned PUT into the quarantine bucket, anchored on the parent submission. */
export function mintPhotoUpload(
  body: { submissionId: string; contentType: string; contentLength: number },
  options: RequestOptions = {},
): Promise<ApiOutcome<PhotoUploadTarget>> {
  return apiRequest<PhotoUploadTarget>('photos/mint', { ...options, method: 'POST', body });
}

/**
 * PUT the bytes to the minted URL.
 *
 * Not `apiRequest`: this goes to S3, not to our API, and it must carry **no Authorization header**.
 * The signature is the authorization, and an extra header changes the signed request and gets a 403
 * that reads like a permissions problem.
 *
 * `Content-Type` and `Content-Length` are signed INTO the URL, so they are sent exactly as the mint
 * response gave them. The browser sets `Content-Length` itself from the body and refuses an explicit
 * one — so we assert the blob's size matches instead, and fail locally with a clear message rather
 * than letting S3 answer with a signature mismatch.
 */
export async function putPhotoBytes(
  target: PhotoUploadTarget,
  blob: Blob,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<ApiOutcome<null>> {
  const expectedLength = Number.parseInt(target.requiredHeaders['Content-Length'], 10);
  if (blob.size !== expectedLength) {
    return {
      kind: 'rejected',
      status: 400,
      code: 'content-length-mismatch',
      requestId: null,
      issues: [{ expected: expectedLength, actual: blob.size }],
    };
  }
  const doFetch = options.fetchImpl ?? globalThis.fetch;
  try {
    const response = await doFetch(target.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': target.requiredHeaders['Content-Type'] },
      body: blob,
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
    if (response.status >= 200 && response.status < 300) {
      return { kind: 'ok', status: response.status, data: null };
    }
    if (response.status >= 500) {
      return { kind: 'server-error', status: response.status, requestId: null };
    }
    // S3 answers XML, not our JSON envelope, so there is no `code` to read. The status is the
    // information; a 403 here is almost always an expired or altered signature.
    return { kind: 'rejected', status: response.status, code: `s3-${response.status}`, requestId: null };
  } catch (error) {
    return { kind: 'network-error', detail: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * The permanent, authorized URL for a photo. Use it as an `<img src>`; do not `fetch` it.
 *
 * `GET /photos/{photoId}` authenticates, re-authorizes through the SAME ownership check as submission
 * reads, then 302s to a freshly minted 120-second signed URL. Letting the browser follow that
 * redirect in an `<img>` is the intended path — and it is why no signed URL is ever stored anywhere.
 *
 * ⚠️ Expect a 503 with blocker `C-42-cloudfront-signing-key` on any stage with no committed signing
 * key. The route refuses rather than handing out an unsigned URL.
 */
export function photoUrl(photoId: string): string {
  return `${PHOTO_BASE_PATH}/${encodeURIComponent(photoId)}`;
}

/**
 * `POST /interventions/{id}/wasted-trip`.
 *
 * ⚠️ **THIS ROUTE IS A THROWING PLACEHOLDER SERVER-SIDE AND WILL NOT WORK.**
 * `infra/lib/config/routes.ts` classifies it `gate-not-implemented` (C-62), and
 * `infra/lib/stacks/api-stack.ts` deliberately deploys an inline Lambda that throws — a property
 * `test/c32-multi-tenant-isolation.test.ts` asserts against the synthesized template, so the route
 * *cannot* serve or write anything until the gate is written.
 *
 * The failure will surface as a **502/500 `server-error`, NOT a `not-built-yet` 503**, because the
 * function throws rather than returning our envelope. That is worth knowing before someone reads the
 * status and concludes the API is broken. It is included here so the shape exists and so this note
 * exists — do not build UI on it.
 */
export function reportWastedTrip(
  interventionId: string,
  body: Readonly<Record<string, unknown>>,
  options: RequestOptions = {},
): Promise<ApiOutcome<unknown>> {
  return apiRequest<unknown>(
    `interventions/${encodeURIComponent(interventionId)}/wasted-trip`,
    { ...options, method: 'POST', body },
  );
}
